import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ExternalLink, Globe, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const SNIPPET_COMPONENTS = {
  p: ({ node, ...props }) => <p className="mb-2 text-sm leading-relaxed text-mute" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-ink" {...props} />,
  em: ({ node, ...props }) => <em className="italic text-mute" {...props} />,
  a: ({ node, children, ...props }) => (
    <a className="text-talent underline underline-offset-2" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
  ),
  ul: ({ node, ...props }) => <ul className="mb-2 list-disc pl-5 text-sm text-mute" {...props} />,
  ol: ({ node, ...props }) => <ol className="mb-2 list-decimal pl-5 text-sm text-mute" {...props} />,
  li: ({ node, ...props }) => <li className="mb-1" {...props} />,
  h1: ({ node, children, ...props }) => <h1 className="mb-1 text-base font-bold text-ink" {...props}>{children}</h1>,
  h2: ({ node, children, ...props }) => <h2 className="mb-1 text-sm font-bold text-ink" {...props}>{children}</h2>,
  h3: ({ node, children, ...props }) => <h3 className="mb-1 text-sm font-bold text-ink" {...props}>{children}</h3>,
  h4: ({ node, children, ...props }) => <h4 className="mb-1 text-sm font-bold text-ink" {...props}>{children}</h4>,
  table: ({ node, ...props }) => <div className="mb-2 overflow-x-auto"><table className="w-full border-collapse text-xs" {...props} /></div>,
  th: ({ node, ...props }) => <th className="border border-shell-border bg-paper-2 px-2 py-1 text-left font-semibold text-ink" {...props} />,
  td: ({ node, ...props }) => <td className="border border-shell-border px-2 py-1 align-top text-mute" {...props} />,
  code: ({ node, inline, ...props }) =>
    inline ? (
      <code className="rounded bg-paper-2 px-1 py-0.5 font-mono text-xs text-[#00dc49]" {...props} />
    ) : (
      <code className="block rounded bg-paper-2 p-2 font-mono text-xs text-ink" {...props} />
    ),
  pre: ({ node, ...props }) => <pre className="mb-2 overflow-x-auto rounded bg-paper-2 p-2" {...props} />,
};

const SourceDrawer = ({ source, onClose }) => {
  useEffect(() => {
    if (!source) return undefined;
    const onKey = event => { if (event.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [source, onClose]);

  const letter = (source?.domain || source?.title || '?').charAt(0).toUpperCase();

  return (
    <AnimatePresence>
      {source && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }} onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'transparent', zIndex: 999 }}
          />
          <motion.aside
            key="card"
            initial={{ x: '110%', y: '-50%', opacity: 0.6 }}
            animate={{ x: 0, y: '-50%', opacity: 1 }}
            exit={{ x: '110%', y: '-50%', opacity: 0.6 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={{ position: 'fixed', top: '50%', right: 24, width: 'min(340px, calc(100vw - 48px))', zIndex: 1000 }}
            className="overflow-hidden rounded-2xl border border-shell-border bg-paper shadow-soft">
            <div className="relative flex h-32 w-full items-center justify-center overflow-hidden bg-gradient-to-br from-paper-2 to-console-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[rgba(0,220,73,0.12)] text-xl font-bold text-[#00dc49] ring-1 ring-[rgba(0,220,73,0.3)]">
                {letter}
              </div>
              {source?.image && (
                <img
                  src={source.image} alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  onError={e => { e.currentTarget.style.display = 'none'; }}
                />
              )}
            </div>

            <div className="p-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 truncate text-xs font-medium text-[#00dc49]">
                  <Globe className="h-3.5 w-3.5 shrink-0" />
                  {source?.domain || 'Source'}
                </span>
                <button
                  type="button" onClick={onClose}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-shell-border text-mute transition-colors hover:text-ink">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <h3 className="mb-2 text-base font-bold leading-snug text-ink">{source?.title}</h3>

              {source?.snippet && (
                <div className="max-h-56 overflow-y-auto">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={SNIPPET_COMPONENTS}>
                    {source?.snippet}
                  </ReactMarkdown>
                </div>
              )}

              {source?.url && (
                <a
                  href={source.url} target="_blank" rel="noopener noreferrer"
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#00dc49] px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-[#00c23e]">
                  Visit <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default SourceDrawer;
